import React from "react";
import { useParams } from "react-router-dom";
import { CreditCard, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import PricingPlanCard from "@/components/billing/PricingPlanCard";
import PricingComparisonTable from "@/components/billing/PricingComparisonTable";
import { useLanguage } from "@/lib/LanguageContext";

const pricingHeroCopy = {
  en: {
    label: "Pricing",
    title: "Free public content or one Premium membership",
    intro: "Explore public marine content for free, or unlock every paid Marstick tool with one simple Premium plan.",
    unlock: "One Premium payment unlocks all premium content and tools.",
    unavailable: "Subscription plans are not available yet.",
  },
  he: {
    label: "מחירים",
    title: "תוכן ציבורי בחינם או מנוי פרימיום אחד",
    intro: "גלו תוכן ימי ציבורי בחינם, או פתחו את כל כלי Marstick בתשלום באמצעות מסלול פרימיום פשוט אחד.",
    unlock: "תשלום פרימיום אחד פותח את כל התוכן והכלים המתקדמים.",
    unavailable: "מסלולי המנוי עדיין אינם זמינים.",
  },
  es: {
    label: "Precios",
    title: "Contenido público gratis o una membresía Premium",
    intro: "Explora contenido marino público gratis o desbloquea todas las herramientas de pago de Marstick con un plan Premium sencillo.",
    unlock: "Un único pago Premium desbloquea todo el contenido y las herramientas premium.",
    unavailable: "Los planes de suscripción aún no están disponibles.",
  },
  it: {
    label: "Prezzi",
    title: "Contenuti pubblici gratuiti o un abbonamento Premium",
    intro: "Esplora contenuti marini pubblici gratis oppure sblocca tutti gli strumenti Marstick a pagamento con un semplice piano Premium.",
    unlock: "Un solo pagamento Premium sblocca tutti i contenuti e gli strumenti premium.",
    unavailable: "I piani di abbonamento non sono ancora disponibili.",
  },
  fr: {
    label: "Tarifs",
    title: "Contenu public gratuit ou un abonnement Premium",
    intro: "Explorez gratuitement le contenu marin public ou débloquez tous les outils payants Marstick avec un seul forfait Premium simple.",
    unlock: "Un seul paiement Premium débloque tout le contenu et tous les outils premium.",
    unavailable: "Les abonnements ne sont pas encore disponibles.",
  },
  de: {
    label: "Preise",
    title: "Kostenlose öffentliche Inhalte oder eine Premium-Mitgliedschaft",
    intro: "Entdecke öffentliche maritime Inhalte kostenlos oder schalte jedes kostenpflichtige Marstick-Tool mit einem einfachen Premium-Tarif frei.",
    unlock: "Eine Premium-Zahlung schaltet alle Premium-Inhalte und Tools frei.",
    unavailable: "Abo-Tarife sind noch nicht verfügbar.",
  },
  pt: {
    label: "Preços",
    title: "Conteúdo público gratuito ou uma assinatura Premium",
    intro: "Explore conteúdo marítimo público gratuitamente ou desbloqueie todas as ferramentas pagas da Marstick com um plano Premium simples.",
    unlock: "Um único pagamento Premium libera todo o conteúdo e ferramentas premium.",
    unavailable: "Os planos de assinatura ainda não estão disponíveis.",
  },
  ar: {
    label: "الأسعار",
    title: "محتوى عام مجاني أو عضوية بريميوم واحدة",
    intro: "استكشف المحتوى البحري العام مجانًا، أو افتح كل أدوات Marstick المدفوعة من خلال خطة بريميوم بسيطة واحدة.",
    unlock: "دفعة بريميوم واحدة تفتح كل المحتوى والأدوات المميزة.",
    unavailable: "خطط الاشتراك غير متاحة بعد.",
  },
  el: {
    label: "Τιμές",
    title: "Δωρεάν δημόσιο περιεχόμενο ή μία συνδρομή Premium",
    intro: "Εξερευνήστε δωρεάν δημόσιο ναυτικό περιεχόμενο ή ξεκλειδώστε κάθε πληρωμένο εργαλείο Marstick με ένα απλό πρόγραμμα Premium.",
    unlock: "Μία πληρωμή Premium ξεκλειδώνει όλο το premium περιεχόμενο και τα εργαλεία.",
    unavailable: "Τα προγράμματα συνδρομής δεν είναι ακόμη διαθέσιμα.",
  },
  ru: {
    label: "Цены",
    title: "Бесплатный публичный контент или одна Premium-подписка",
    intro: "Изучайте публичный морской контент бесплатно или откройте все платные инструменты Marstick с одним простым Premium-планом.",
    unlock: "Один платеж Premium открывает весь премиум-контент и инструменты.",
    unavailable: "Планы подписки пока недоступны.",
  },
  tr: {
    label: "Fiyatlandırma",
    title: "Ücretsiz herkese açık içerik veya tek Premium üyelik",
    intro: "Herkese açık denizcilik içeriklerini ücretsiz keşfedin ya da tek bir Premium planla tüm ücretli Marstick araçlarını açın.",
    unlock: "Tek bir Premium ödeme, tüm premium içerik ve araçları açar.",
    unavailable: "Abonelik planları henüz kullanılamıyor.",
  },
  zh: {
    label: "价格",
    title: "免费公共内容或一个高级会员",
    intro: "免费浏览公开海事内容，或通过一个简单的高级计划解锁所有付费 Marstick 工具。",
    unlock: "一次高级会员付款即可解锁所有高级内容和工具。",
    unavailable: "订阅方案尚不可用。",
  },
};

export default function PricingComparisonSection({ plans, currentTier, copy, paypalCopy }) {
  const { lang: routeLang } = useParams();
  const { language } = useLanguage();
  const lang = routeLang || language || "en";
  const hero = pricingHeroCopy[lang] || pricingHeroCopy.en;
  const sortedPlans = [...plans]
    .filter((plan, index, all) => all.findIndex((item) => item.tier === plan.tier) === index)
    .sort((a, b) => (a.monthly_price || 0) - (b.monthly_price || 0));

  if (!sortedPlans.length) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">{hero.unavailable}</CardContent>
      </Card>
    );
  }

  const popularTier = "premium";

  return (
    <div className="space-y-8">
      <section className="overflow-hidden rounded-[2rem] border border-primary/20 bg-gradient-to-br from-ocean via-atlantic to-primary p-8 text-white shadow-2xl shadow-ocean/20 md:p-10">
        <div className="max-w-3xl">
          <p className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/12 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] !text-cyan-100">
            <CreditCard className="h-4 w-4" /> {hero.label}
          </p>
          <h2 className="font-heading text-4xl font-black tracking-tight !text-white md:text-5xl">{hero.title}</h2>
          <p className="mt-4 max-w-2xl text-base leading-8 !text-cyan-50/85">
            {hero.intro}
          </p>
          <p className="mt-5 inline-flex items-center gap-2 text-sm font-semibold !text-cyan-50">
            <ShieldCheck className="h-4 w-4" /> {hero.unlock}
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:gap-8">
        {sortedPlans.map((plan) => (
          <PricingPlanCard
            key={plan.id || plan.tier}
            plan={plan}
            isCurrent={plan.tier === currentTier}
            isPopular={plan.tier === popularTier && plan.tier !== currentTier}
            copy={copy}
            paypalCopy={paypalCopy}
          />
        ))}
      </div>

      <PricingComparisonTable plans={sortedPlans} copy={copy} />
    </div>
  );
}